import React, { useState } from "react";
import Marquee from "react-fast-marquee";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Star, Check, X, Heart, MessageCircle, Send, Bookmark } from "lucide-react";

// List of all Social Media Post assets split into Top, Middle, and Bottom Rows
const FEATURED_POSTS = [
  {
    image: "/images/films/1930-awards-poster.jpg",
    title: "1930 — Official Awards Poster",
    client: "1930 Cybercrime Awareness",
    isFeatured: true
  },
  {
    image: "/images/films/two-wombs-one-story-poster.jpg",
    title: "Two Wombs, One Story — Campaign",
    client: "Vanitaa Vins Presents",
    isFeatured: true
  },
  {
    image: "/works/Social Media Posts/vanitaapande-post.png",
    title: "Minimalist Brand Showcase",
    client: "Vanitaa Pande",
    isFeatured: true
  },
  {
    image: "/works/Social Media Posts/vanitaapande-post2.png",
    title: "Luxury Style Identity",
    client: "Vanitaa Pande",
    isFeatured: true
  }
];

const TOP_ROW_POSTS = [
  {
    image: "/works/Social Media Posts/classic-motor-post.png",
    title: "Vintage Campaign",
    client: "Classic Motors"
  },
  {
    image: "/works/Social Media Posts/flarebyblaze-post1.png",
    title: "Bold Creative Layout",
    client: "Flare by Blaze"
  },
  {
    image: "/works/Social Media Posts/gangan-post1.png",
    title: "Aesthetic Editorial",
    client: "Gangan Studios"
  },
  {
    image: "/works/Social Media Posts/saanvi-ethnicos-post1.png",
    title: "Cultural Brand Story",
    client: "Saanvi Ethnicos"
  },
  {
    image: "/works/Social Media Posts/sajwat-post.png",
    title: "Luxury Decor Carousel",
    client: "Sajwat Designs"
  },
  {
    image: "/works/Social Media Posts/vow.aestheticswellness-post.png",
    title: "Zen Wellness Identity",
    client: "VOW Aesthetics"
  }
];

const BOTTOM_ROW_POSTS = [
  {
    image: "/works/Social Media Posts/classic-motor-post2.png",
    title: "Heritage Auto Launch",
    client: "Classic Motors"
  },
  {
    image: "/works/Social Media Posts/flarebyblaze-post2.png",
    title: "Premium Launch Grid",
    client: "Flare by Blaze"
  },
  {
    image: "/works/Social Media Posts/saanvi-ethnicos-post2.png",
    title: "Festive Collection Visual",
    client: "Saanvi Ethnicos"
  },
  {
    image: "/works/Social Media Posts/sajwat-post2.png",
    title: "Modern Minimal Interior",
    client: "Sajwat Designs"
  },
  {
    image: "/works/Social Media Posts/sajwat-post3.png",
    title: "Bespoke Collection Layout",
    client: "Sajwat Designs"
  },
  {
    image: "/works/Social Media Posts/vow.aestheticswellness-post1.png",
    title: "Holistic Health Grid",
    client: "VOW Aesthetics"
  }
];

// Compact Marquee Post Card
const CompactPostCard = ({ post, index, prefix, onClick }) => {
  const [loaded, setLoaded] = useState(false);
  const displayLabel = `${prefix}-${String(index + 1).padStart(2, "0")}`;
  return (
    <div
      onClick={onClick}
      className="flex-shrink-0 w-[170px] md:w-[210px] bg-black/60 backdrop-blur-md rounded-2xl overflow-hidden border border-white/5 hover:border-[var(--gold)]/40 hover:-translate-y-1.5 transition-all duration-500 cursor-pointer group mx-3 my-2"
    >
      <div className="relative aspect-square overflow-hidden bg-[#0d0c0a]">
        {!loaded && (
          <div className="absolute inset-0 bg-gradient-to-r from-neutral-950 via-neutral-900 to-neutral-950 animate-pulse z-10" />
        )}
        <img
          src={post.image}
          alt={post.title}
          onLoad={() => setLoaded(true)}
          className={`w-full h-full object-cover transition-all duration-700 group-hover:scale-105 ${loaded ? "opacity-100" : "opacity-0"}`}
        />
        {/* Glow overlay */}
        <div className="absolute inset-0 bg-black/15 group-hover:bg-black/0 transition-colors" />
        {/* Client Tag */}
        <div className="absolute top-2 right-2 bg-black/75 px-2 py-0.5 rounded-md border border-white/10 text-[7px] font-impact text-[var(--gold)] uppercase tracking-widest z-20">
          {post.client.split(" ")[0]}
        </div>
      </div>
      <div className="p-3 bg-[#0a0908] text-center border-t border-white/[0.03]">
        <span className="block text-[8px] font-impact tracking-widest text-[var(--gold)] mb-0.5">
          {displayLabel}
        </span>
        <h4 className="text-[10px] font-display text-[var(--cream)] truncate group-hover:text-[var(--gold-bright)] transition-colors">
          {post.title}
        </h4>
      </div>
    </div>
  );
};

// Luxury Featured Card
const FeaturedPostCard = ({ post, index, onClick }) => {
  const [loaded, setLoaded] = useState(false);
  const firstLetter = post.client.charAt(0);
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.6, delay: index * 0.15 }}
      onClick={onClick}
      className="flex flex-col items-center group cursor-pointer w-full max-w-[340px] mx-auto"
    >
      {/* Matte print mockup frame with gold backing shadow */}
      <div className="relative w-full aspect-square bg-[#0b0a09] rounded-[32px] p-3 border border-white/10 shadow-[0_15px_45px_rgba(0,0,0,0.8)] ring-1 ring-[var(--gold)]/20 overflow-hidden flex flex-col z-20 transition-all duration-500 group-hover:shadow-[0_0_50px_rgba(201,169,97,0.3)] group-hover:ring-[var(--gold)] group-hover:-translate-y-2">

        {/* Header Profile mockup inside frame */}
        <div className="flex items-center justify-between px-2 pb-2.5 bg-transparent border-b border-white/[0.05] z-20">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-impact bg-gradient-to-br from-[var(--gold)] to-[var(--copper)] text-black">
              {firstLetter}
            </div>
            <div className="flex items-center gap-1">
              <span className="font-display text-[12px] font-medium text-[var(--cream)] group-hover:text-[var(--gold-bright)] transition-colors">
                {post.client}
              </span>
              <span className="w-3 h-3 rounded-full bg-sky-500 flex items-center justify-center text-[7px] text-white">
                <Check size={6} strokeWidth={4} />
              </span>
            </div>
          </div>
          <span className="px-2.5 py-0.5 rounded-full bg-[var(--gold)]/10 border border-[var(--gold)]/30 text-[var(--gold)] text-[8px] font-impact tracking-wider uppercase flex items-center gap-1 animate-pulse">
            <Star size={8} fill="currentColor" /> Featured
          </span>
        </div>

        {/* Viewport for the static work */}
        <div className="relative flex-1 rounded-[22px] overflow-hidden bg-[#0d0c0a] mt-3 flex items-center justify-center z-20 border border-white/[0.03]">
          {!loaded && (
            <div className="absolute inset-0 bg-gradient-to-r from-neutral-950 via-neutral-900 to-neutral-950 animate-pulse z-10" />
          )}
          <img
            src={post.image}
            alt={post.title}
            onLoad={() => setLoaded(true)}
            className={`w-full h-full object-cover transition-all duration-700 group-hover:scale-105 ${loaded ? "opacity-100" : "opacity-0"}`}
          />
          {/* Hover overlay */}
          <div className="absolute inset-0 bg-black/35 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center backdrop-blur-[1.5px] z-35">
            <div className="w-11 h-11 rounded-full bg-black/60 border border-white/10 backdrop-blur-md flex items-center justify-center text-white scale-90 group-hover:scale-100 transition-all duration-500 shadow-2xl">
              <Sparkles size={16} className="text-[var(--gold-bright)]" />
            </div>
          </div>
        </div>

        {/* Caption bar */}
        <div className="pt-2 px-1 text-left z-20">
          <p className="text-[11px] text-[var(--cream-muted)] leading-relaxed font-body mt-1 truncate">
            <span className="font-semibold text-[var(--cream)] mr-1.5">{post.client}</span>
            {post.title}
          </p>
        </div>

      </div>
    </motion.div>
  );
};

const SocialPostsShowcase = () => {
  const [selectedPost, setSelectedPost] = useState(null);

  return (
    <section id="featured-posts" className="py-32 relative overflow-hidden bg-[var(--ink-2)] border-t border-[var(--rule)]">
      {/* Background Decorative Lighting */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-[radial-gradient(circle_at_center,rgba(201,169,97,0.03)_0%,transparent_70%)] pointer-events-none" />

      <div className="w-full relative z-10">

        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-20 px-6">
          <div className="flex items-center justify-center gap-4 mb-4">
            <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--gold)]">WORKS-03</span>
            <span className="w-16 h-px bg-[var(--rule)]" />
            <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--cream-muted)] uppercase">Campaign Creatives</span>
          </div>
          <h2 className="font-display text-5xl md:text-7xl text-[var(--cream)] leading-tight">
            Creative <span className="italic gold-text">Posts</span> Portfolio
          </h2>
          <p className="mt-6 text-[var(--cream-muted)] text-base md:text-lg font-body leading-relaxed">
            Curated visual grids, typographic layouts, and social media post campaigns. Hover to pause auto-scroll, or view our featured works in full focus below.
          </p>
        </div>

        {/* 1. TOP ROW: Infinite Horizontal Marquee (Left to Right) */}
        <div className="w-full mb-16 relative overflow-hidden py-4 border-y border-[var(--rule)] bg-black/10">
          <Marquee
            direction="right"
            speed={40}
            pauseOnHover={true}
            gradient={true}
            gradientColor="#0a0908"
            gradientWidth={80}
          >
            {TOP_ROW_POSTS.map((post, idx) => (
              <CompactPostCard
                key={idx}
                post={post}
                index={idx}
                prefix="GRID"
                onClick={() => setSelectedPost(post)}
              />
            ))}
          </Marquee>
        </div>

        {/* 2. MIDDLE SECTION: Featured 3-Card Grid */}
        <div className="container-xl px-6 mb-16">
          <div className="text-center mb-12">
            <h3 className="font-impact text-xl sm:text-2xl tracking-[0.3em] text-[var(--gold)] uppercase flex items-center justify-center gap-2">
              <Sparkles size={16} /> Featured Posts <Sparkles size={16} />
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12 max-w-6xl mx-auto">
            {FEATURED_POSTS.map((post, idx) => (
              <FeaturedPostCard
                key={idx}
                post={post}
                index={idx}
                onClick={() => setSelectedPost(post)}
              />
            ))}
          </div>
        </div>

        {/* 3. BOTTOM ROW: Infinite Horizontal Marquee (Right to Left) */}
        <div className="w-full mt-16 relative overflow-hidden py-4 border-y border-[var(--rule)] bg-black/10">
          <Marquee
            direction="left"
            speed={40}
            pauseOnHover={true}
            gradient={true}
            gradientColor="#0a0908"
            gradientWidth={80}
          >
            {BOTTOM_ROW_POSTS.map((post, idx) => (
              <CompactPostCard
                key={idx}
                post={post}
                index={idx}
                prefix="DESIGN"
                onClick={() => setSelectedPost(post)}
              />
            ))}
          </Marquee>
        </div>

        {/* Section Footer Callout */}
        <div className="mt-16 text-center px-6">
          <p className="text-[11px] text-[var(--muted)] italic font-body flex items-center justify-center gap-1.5">
            <Sparkles size={12} className="text-[var(--gold)]" /> Tap any image grid to view the static visual asset in full high definition.
          </p>
        </div>

      </div>

      {/* 4. PREMIUM FULLSCREEN POST LIGHTBOX MODAL */}
      <AnimatePresence>
        {selectedPost && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-black/90 backdrop-blur-xl"
            onClick={() => setSelectedPost(null)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 30 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 30 }}
              transition={{ type: "spring", damping: 26, stiffness: 175 }}
              className="relative w-full max-w-[500px] bg-black rounded-[32px] border border-white/10 shadow-[0_0_50px_rgba(201,169,97,0.3)] overflow-hidden flex flex-col z-20"
              onClick={(e) => e.stopPropagation()} // Prevent close on clicking post
            >
              {/* Profile Header in Lightbox */}
              <div className="flex items-center justify-between p-4 border-b border-white/[0.05]">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-impact ${selectedPost.isFeatured
                      ? "bg-gradient-to-br from-[var(--gold)] to-[var(--copper)] text-black"
                      : "bg-white/[0.02] text-[var(--gold)]"
                    }`}>
                    {selectedPost.client.charAt(0)}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="font-display text-sm font-semibold text-[var(--cream)]">
                      {selectedPost.client}
                    </span>
                    <span className="w-3.5 h-3.5 rounded-full bg-sky-500 flex items-center justify-center text-[8px] text-white">
                      <Check size={8} strokeWidth={4} />
                    </span>
                  </div>
                </div>

                <button
                  onClick={() => setSelectedPost(null)}
                  className="p-1.5 hover:bg-white/10 rounded-full text-white/70 hover:text-white transition-colors focus:outline-none"
                  title="Close Preview"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Large Image Box */}
              <div className="relative w-full aspect-square bg-black overflow-hidden flex items-center justify-center border-b border-white/[0.05]">
                <img
                  src={selectedPost.image}
                  alt={selectedPost.title}
                  className="w-full h-full object-contain"
                />
              </div>

              {/* Details in Lightbox */}
              <div className="p-5 text-left bg-black">
                <h4 className="font-display text-lg text-[var(--gold)] mb-1.5">{selectedPost.title}</h4>
                <p className="text-[12px] text-[var(--cream-muted)] leading-relaxed font-body">
                  Custom-tailored, scroll-stopping social media visual assets designed and curated exclusively for {selectedPost.client}.
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default SocialPostsShowcase;
